import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Laptop, MonitorSmartphone, Pencil, Plus, Smartphone, Star, Trash2,
} from 'lucide-react'
import {
  formatDateTime, formatNumber, PERMISSIONS, registerSchema, storeSchema,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, ConfirmDialog, Dialog, EmptyState, Input,
  Select, Spinner, Switch, Table, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import {
  devicesApi, registersApi, storesApi, type DeviceRow, type RegisterRow, type StoreRow,
} from '../lib/settingsApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

/* ── نافذة الفرع ── */
function StoreDialog({ open, onClose, store }: { open: boolean; onClose: () => void; store: StoreRow | null }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()
  const [form, setForm] = useState({ code: '', nameAr: '', address: '', phone: '', isActive: true })

  useEffect(() => {
    if (!open) return
    clear()
    setForm({
      code: store?.code ?? '',
      nameAr: store?.nameAr ?? '',
      address: store?.address ?? '',
      phone: store?.phone ?? '',
      isActive: store?.isActive ?? true,
    })
  }, [open, store, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      if (store) {
        const { code: _code, ...patch } = form
        return storesApi.update(store.id, patch)
      }
      const parsed = validate(storeSchema, { ...form, code: form.code.toUpperCase() })
      if (!parsed) throw new Error('VALIDATION')
      return storesApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stores'] })
      toast.success(store ? 'حُفظ الفرع' : 'أُنشئ الفرع')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ الفرع')
    },
  })

  return (
    <Dialog
      open={open} onClose={onClose} size="md"
      title={store ? `تعديل: ${store.nameAr}` : 'فرع جديد'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="اسم الفرع" required autoFocus
            value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
            error={errors.nameAr}
          />
          <Input
            label="الرمز" required ltr disabled={!!store}
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            error={errors.code}
            hint={store ? 'الرمز لا يُعدَّل — يظهر في أرقام الفواتير المطبوعة' : 'مثل MAIN — يدخل في رقم الفاتورة'}
          />
          <Input
            label="الهاتف" ltr
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            error={errors.phone}
          />
          <Input
            label="العنوان"
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            error={errors.address}
          />
        </div>
        <Switch
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          label="فرع نشط"
          description="التعطيل يُخفيه من فتح الورديات والتحويلات، ويُبقي تاريخه في التقارير"
        />
      </div>
    </Dialog>
  )
}

/* ── نافذة الصندوق ── */
function RegisterDialog({
  open, onClose, register, stores,
}: { open: boolean; onClose: () => void; register: RegisterRow | null; stores: StoreRow[] }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()
  const [form, setForm] = useState({ storeId: '', code: '', nameAr: '', isActive: true })

  useEffect(() => {
    if (!open) return
    clear()
    setForm({
      storeId: register?.storeId ?? stores.find((s) => s.isDefault)?.id ?? stores[0]?.id ?? '',
      code: register?.code ?? '',
      nameAr: register?.nameAr ?? '',
      isActive: register?.isActive ?? true,
    })
  }, [open, register, stores, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      if (register) {
        return registersApi.update(register.id, { nameAr: form.nameAr, isActive: form.isActive })
      }
      const parsed = validate(registerSchema, { ...form, code: form.code.toUpperCase() })
      if (!parsed) throw new Error('VALIDATION')
      return registersApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['registers'] })
      void queryClient.invalidateQueries({ queryKey: ['stores'] })
      toast.success(register ? 'حُفظ الصندوق' : 'أُنشئ الصندوق')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ الصندوق')
    },
  })

  return (
    <Dialog
      open={open} onClose={onClose} size="md"
      title={register ? `تعديل: ${register.nameAr}` : 'صندوق جديد'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}
        <Select
          label="الفرع" required disabled={!!register}
          value={form.storeId} onChange={(e) => setForm({ ...form, storeId: e.target.value })}
          options={stores.filter((s) => s.isActive || s.id === form.storeId)
            .map((s) => ({ value: s.id, label: `${s.nameAr} (${s.code})` }))}
          error={errors.storeId}
          hint={register ? 'الصندوق لا يُنقل بين الفروع — ورديّاته ومبيعاته مرتبطة بمخزون فرعه' : undefined}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="اسم الصندوق" required autoFocus
            value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
            error={errors.nameAr}
          />
          <Input
            label="الرمز" required ltr disabled={!!register}
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            error={errors.code}
            hint={register ? undefined : 'مثل 01 — يدخل في رقم الفاتورة'}
          />
        </div>
        <Switch
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          label="صندوق نشط"
          description="التعطيل يمنع فتح ورديات جديدة عليه"
        />
      </div>
    </Dialog>
  )
}

/* ── نافذة الجهاز ── */
function DeviceDialog({
  device, onClose, stores,
}: { device: DeviceRow | null; onClose: () => void; stores: StoreRow[] }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { formError, handleApiError } = useFormErrors()
  const [nameAr, setNameAr] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!device) return
    setNameAr(device.nameAr)
    setIsActive(device.isActive)
  }, [device])

  const mutation = useMutation({
    mutationFn: () => devicesApi.update(device!.id, { nameAr, isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('حُفظ الجهاز')
      onClose()
    },
    onError: (err) => handleApiError(err, 'تعذّر حفظ الجهاز'),
  })

  return (
    <Dialog
      open={!!device} onClose={onClose} size="md"
      title="الجهاز" description={device?.deviceKey}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}
        <Input label="اسم الجهاز" required autoFocus value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
        {device && (
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-bg-subtle p-3 text-sm">
            <div>
              <p className="text-xs text-fg-subtle">الفرع</p>
              <p className="text-fg">{device.storeName ?? stores.find((s) => s.isDefault)?.nameAr ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">فواتير دفعها</p>
              <p className="tabular-nums text-fg">{formatNumber(device.salesCount)}</p>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">مؤشّر المزامنة</p>
              <p className="font-mono text-fg" dir="ltr">{device.syncCursor}</p>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">آخر ظهور</p>
              <p className="text-fg">{device.lastSeenAt ? formatDateTime(device.lastSeenAt) : 'لم يُزامن بعد'}</p>
            </div>
          </div>
        )}
        <Switch
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          label="جهاز نشط"
          description="تعطيل جهاز مفقود يقطع مزامنته — لكن الفواتير التي باعها ولم تصل تُفقَد"
        />
      </div>
    </Dialog>
  )
}

export function BranchesPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.SETTINGS_MANAGE)

  const [storeDialog, setStoreDialog] = useState<{ open: boolean; store: StoreRow | null }>({ open: false, store: null })
  const [registerDialog, setRegisterDialog] = useState<{ open: boolean; register: RegisterRow | null }>({ open: false, register: null })
  const [device, setDevice] = useState<DeviceRow | null>(null)
  const [confirm, setConfirm] = useState<{ kind: 'store' | 'register' | 'device'; id: string; name: string } | null>(null)

  const stores = useQuery({ queryKey: ['stores'], queryFn: storesApi.list })
  const registers = useQuery({ queryKey: ['registers'], queryFn: registersApi.list })
  const devices = useQuery({ queryKey: ['devices'], queryFn: devicesApi.list, enabled: canManage })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['stores'] })
    void queryClient.invalidateQueries({ queryKey: ['registers'] })
    void queryClient.invalidateQueries({ queryKey: ['devices'] })
  }

  const defaultMutation = useMutation({
    mutationFn: (id: string) => storesApi.makeDefault(id),
    onSuccess: () => { invalidate(); toast.success('تغيّر الفرع الافتراضي') },
    onError: (err) => toast.error('تعذّر التغيير', err instanceof Error ? err.message : undefined),
  })

  const removeMutation = useMutation({
    mutationFn: ({ kind, id }: { kind: 'store' | 'register' | 'device'; id: string }) =>
      kind === 'store' ? storesApi.remove(id) : kind === 'register' ? registersApi.remove(id) : devicesApi.remove(id),
    onSuccess: () => { invalidate(); toast.success('تمّ الحذف'); setConfirm(null) },
    onError: (err) => {
      toast.error('تعذّر الحذف', err instanceof Error ? err.message : undefined)
      setConfirm(null)
    },
  })

  if (!canManage) {
    return <Alert tone="danger" title="غير مصرّح">إدارة الفروع تتطلّب صلاحية «إدارة الإعدادات».</Alert>
  }

  if (stores.isPending || registers.isPending) {
    return <div className="grid h-64 place-items-center"><Spinner className="size-7 text-primary" /></div>
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-fg">الفروع والصناديق</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          الفرع وحدة مخزون مستقلّة، والصندوق نقطة بيع داخله
        </p>
      </div>

      {/* ── الفروع ── */}
      <Card>
        <CardHeader
          title="الفروع"
          description="لكل فرع أرصدته وحركاته — النقل بينها يكون بتحويل مخزون، لا بتعديل رقم"
          action={
            <Button size="sm" startIcon={<Plus className="size-4" />} onClick={() => setStoreDialog({ open: true, store: null })}>
              فرع جديد
            </Button>
          }
        />
        <CardBody className="p-0">
          <TableWrapper className="border-0">
            <Table>
              <Thead>
                <tr>
                  <Th>الفرع</Th>
                  <Th>الرمز</Th>
                  <Th align="center">الصناديق</Th>
                  <Th align="center">الموظّفون</Th>
                  <Th align="center">وحدات بالمخزون</Th>
                  <Th align="center">إجراءات</Th>
                </tr>
              </Thead>
              <Tbody>
                {stores.data?.map((s) => (
                  <Tr key={s.id} className={cn(!s.isActive && 'opacity-55')}>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Building2 className="size-4 shrink-0 text-fg-subtle" />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-fg">{s.nameAr}</p>
                          {s.address && <p className="truncate text-xs text-fg-subtle">{s.address}</p>}
                        </div>
                        {s.isDefault && <Badge tone="primary" className="shrink-0">افتراضي</Badge>}
                        {!s.isActive && <Badge tone="neutral" className="shrink-0">معطّل</Badge>}
                      </div>
                    </Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{s.code}</Td>
                    <Td align="center" className="tabular-nums">{formatNumber(s.registerCount)}</Td>
                    <Td align="center" className="tabular-nums">{formatNumber(s.userCount)}</Td>
                    <Td align="center" className="tabular-nums">{formatNumber(s.stockQty)}</Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        {!s.isDefault && s.isActive && (
                          <Button
                            variant="ghost" size="icon" title="اجعله الفرع الافتراضي"
                            loading={defaultMutation.isPending && defaultMutation.variables === s.id}
                            onClick={() => defaultMutation.mutate(s.id)}
                          >
                            <Star className="size-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title="تعديل" onClick={() => setStoreDialog({ open: true, store: s })}>
                          <Pencil className="size-4" />
                        </Button>
                        {!s.isDefault && (
                          <Button
                            variant="ghost" size="icon" title="حذف"
                            className="text-danger hover:bg-danger-subtle"
                            onClick={() => setConfirm({ kind: 'store', id: s.id, name: s.nameAr })}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        </CardBody>
      </Card>

      {/* ── الصناديق ── */}
      <Card>
        <CardHeader
          title="الصناديق"
          description="الوردية تُفتح على صندوق، وجرد الإغلاق يخصّه وحده"
          action={
            <Button
              size="sm" startIcon={<Plus className="size-4" />}
              onClick={() => setRegisterDialog({ open: true, register: null })}
              disabled={!stores.data?.some((s) => s.isActive)}
            >
              صندوق جديد
            </Button>
          }
        />
        <CardBody className="p-0">
          <TableWrapper className="border-0">
            <Table>
              <Thead>
                <tr>
                  <Th>الصندوق</Th>
                  <Th>الفرع</Th>
                  <Th>الرمز</Th>
                  <Th>الوردية الحالية</Th>
                  <Th align="center">إجراءات</Th>
                </tr>
              </Thead>
              <Tbody>
                {registers.data?.map((r) => (
                  <Tr key={r.id} className={cn(!r.isActive && 'opacity-55')}>
                    <Td>
                      <span className="font-medium text-fg">{r.nameAr}</span>
                      {!r.isActive && <Badge tone="neutral" className="ms-2">معطّل</Badge>}
                    </Td>
                    <Td className="text-sm text-fg-muted">{r.storeName}</Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{r.storeCode}-{r.code}</Td>
                    <Td>
                      {r.openShift ? (
                        <div className="text-xs">
                          <Badge tone="success" dot>مفتوحة</Badge>
                          <span className="ms-2 text-fg-muted">
                            {r.openShift.userName ?? '—'} · {formatDateTime(r.openShift.openedAt)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-fg-subtle">مُغلق</span>
                      )}
                    </Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" title="تعديل" onClick={() => setRegisterDialog({ open: true, register: r })}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" title="حذف"
                          className="text-danger hover:bg-danger-subtle"
                          onClick={() => setConfirm({ kind: 'register', id: r.id, name: r.nameAr })}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {registers.data?.length === 0 && (
              <EmptyState icon={<MonitorSmartphone className="size-10" />} title="لا صناديق بعد" />
            )}
          </TableWrapper>
        </CardBody>
      </Card>

      {/* ── الأجهزة ── */}
      <Card>
        <CardHeader
          title="الأجهزة"
          description="تُسجّل نفسها عند أول مزامنة — لا تُضاف من هنا"
        />
        <CardBody className="space-y-3 p-0">
          <TableWrapper className="border-0">
            <Table>
              <Thead>
                <tr>
                  <Th>الجهاز</Th>
                  <Th>المنصّة</Th>
                  <Th align="center">فواتير</Th>
                  <Th align="center">مؤشّر المزامنة</Th>
                  <Th>آخر ظهور</Th>
                  <Th align="center">إجراءات</Th>
                </tr>
              </Thead>
              <Tbody>
                {devices.data?.map((d) => (
                  <Tr key={d.id} className={cn(!d.isActive && 'opacity-55')}>
                    <Td>
                      <div className="flex items-center gap-2">
                        {d.platform === 'android' || d.platform === 'ios'
                          ? <Smartphone className="size-4 shrink-0 text-fg-subtle" />
                          : <Laptop className="size-4 shrink-0 text-fg-subtle" />}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-fg">
                            {d.nameAr}
                            {!d.isActive && <Badge tone="neutral" className="ms-2">معطّل</Badge>}
                          </p>
                          <p className="truncate font-mono text-[10px] text-fg-subtle" dir="ltr">{d.deviceKey}</p>
                        </div>
                      </div>
                    </Td>
                    <Td className="text-xs text-fg-muted">{d.platform ?? '—'}</Td>
                    <Td align="center" className="tabular-nums">{formatNumber(d.salesCount)}</Td>
                    <Td align="center" className="font-mono text-xs text-fg-muted" dir="ltr">
                      {d.syncCursor}
                    </Td>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">
                      {d.lastSeenAt ? formatDateTime(d.lastSeenAt) : 'لم يُزامن بعد'}
                    </Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" title="تعديل" onClick={() => setDevice(d)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" title="حذف"
                          className="text-danger hover:bg-danger-subtle"
                          onClick={() => setConfirm({ kind: 'device', id: d.id, name: d.nameAr })}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {devices.data?.length === 0 && (
              <EmptyState
                icon={<Laptop className="size-10" />}
                title="لا أجهزة مسجّلة"
                description="جهاز سطح المكتب يُسجّل نفسه عند أول مزامنة"
              />
            )}
          </TableWrapper>
        </CardBody>
      </Card>

      <StoreDialog
        open={storeDialog.open}
        onClose={() => setStoreDialog({ open: false, store: null })}
        store={storeDialog.store}
      />
      <RegisterDialog
        open={registerDialog.open}
        onClose={() => setRegisterDialog({ open: false, register: null })}
        register={registerDialog.register}
        stores={stores.data ?? []}
      />
      <DeviceDialog device={device} onClose={() => setDevice(null)} stores={stores.data ?? []} />

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && removeMutation.mutate({ kind: confirm.kind, id: confirm.id })}
        loading={removeMutation.isPending}
        title="تأكيد الحذف"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{confirm?.name}</span>.
            <br />
            {confirm?.kind === 'device'
              ? 'الجهاز الذي دفع فواتير لا يُحذف — عطّله بدل ذلك.'
              : 'ما تحرّك مخزونه أو بيعه لا يُحذف — عطّله ليبقى تاريخه في التقارير.'}
          </>
        }
      />
    </div>
  )
}
